import { format, intervalToDuration, startOfDay, subMinutes } from "date-fns";

/**
 * Formats a duration between two dates into a human-readable "X time remaining" string.
 * Replaces moment.duration(A.diff(B)).humanize()
 *
 * @param {Date|string} endDate - The future end date
 * @param {Date|string} [startDate=new Date()] - The start date (defaults to now)
 * @param {boolean} [withPrefix=false] - If true, prepends "in " (like moment.humanize(true))
 * @returns {string} e.g. "5 days", "3 hours", "in 5 days"
 */
export function formatTimeRemaining(endDate, startDate = new Date(), withPrefix = false) {
  const end = new Date(endDate);
  const start = new Date(startDate);

  if (end <= start) {
    return withPrefix ? "now" : "0 days";
  }

  const duration = intervalToDuration({ start, end });

  // Pick the largest non-zero unit
  if (duration.years > 0) {
    const val = duration.years;
    const str = `${val} year${val > 1 ? "s" : ""}`;
    return withPrefix ? `in ${str}` : str;
  }
  if (duration.months > 0) {
    const val = duration.months;
    const str = `${val} month${val > 1 ? "s" : ""}`;
    return withPrefix ? `in ${str}` : str;
  }
  if (duration.days > 0) {
    const val = duration.days;
    const str = `${val} day${val > 1 ? "s" : ""}`;
    return withPrefix ? `in ${str}` : str;
  }
  if (duration.hours > 0) {
    const val = duration.hours;
    const str = `${val} hour${val > 1 ? "s" : ""}`;
    return withPrefix ? `in ${str}` : str;
  }
  if (duration.minutes > 0) {
    const val = duration.minutes;
    const str = `${val} minute${val > 1 ? "s" : ""}`;
    return withPrefix ? `in ${str}` : str;
  }

  return withPrefix ? "in a few seconds" : "a few seconds";
}

/**
 * Formats a date to a string using date-fns format tokens.
 * Convenience wrapper since moment and date-fns format tokens differ.
 *
 * Common token mappings:
 *   DD → dd,  MM → MM,  YYYY → yyyy,  YY → yy,  MMM → MMM
 *   HH → HH,  mm → mm,  ss → ss,  A → a,  SS → SSS
 *
 * @param {Date|string} date
 * @param {string} dateFnsFormat - date-fns format string (e.g. "dd/MM/yyyy")
 * @returns {string}
 */
export function formatDate(date, dateFnsFormat) {
  return format(new Date(date), dateFnsFormat);
}

/**
 * Converts moment-style format tokens to date-fns style format tokens.
 *
 * The two libraries disagree on case in a way that is not merely cosmetic —
 * date-fns treats the uppercase forms as different fields entirely and throws
 * rather than guessing:
 *
 *   moment  date-fns  meaning                 uppercase means in date-fns
 *   DD      dd        day of month, padded    D  = day of year
 *   D       d         day of month            D  = day of year
 *   YYYY    yyyy      calendar year           YYYY = ISO week-numbering year
 *   YY      yy        calendar year, 2-digit  YY   = ISO week-numbering year
 *
 * Order matters: the longer token is replaced first so the shorter pattern
 * cannot match inside it, and each replacement lowercases its match so a later
 * pass cannot touch it again. That makes this safe with any separator,
 * including none at all ("DDMMYY" -> "ddMMyy").
 *
 * MM and MMM are identical in both libraries and are deliberately left alone.
 *
 * Scope is limited to the date tokens this app actually offers. Time tokens are
 * not handled: nothing configures them, and the previous attempt at them was
 * wrong anyway (it expanded "SSS" to "SSSS").
 */
export function momentFormatToDateFns(momentFormat) {
  if (!momentFormat) return "";

  return momentFormat
    .replace(/YYYY/g, "yyyy")
    .replace(/YY/g, "yy")
    .replace(/DD/g, "dd")
    .replace(/D/g, "d");
}

/**
 * Replaces moment().format("MM"), moment().format("YYYY") etc used in placeholder/dateValues objects.
 */
export function getCurrentDateValues() {
  const now = new Date();
  return {
    DD: format(now, "dd"),
    MM: format(now, "MM"),
    YYYY: format(now, "yyyy"),
    YY: format(now, "yy"),
    MMM: format(now, "MMM"),
  };
}

/**
 * Converts a local date to UTC by subtracting the timezone offset.
 * Replaces moment(date).subtract(moment().utcOffset(), "minutes").toDate()
 */
export function convertToUtc(date) {
  const d = new Date(date);
  // moment().utcOffset() = -new Date().getTimezoneOffset()
  return subMinutes(d, -d.getTimezoneOffset());
}

/**
 * Snaps a date to local midnight then converts to UTC midnight at the same clock values.
 * Replaces moment(date).startOf("day").utcOffset(0, true).toDate()
 */
export function toUtcMidnight(date) {
  const d = startOfDay(new Date(date));
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}
